import * as React from 'react';
import {
  Box, Button, Stack, Typography, Stepper, Step, StepLabel,
  ToggleButtonGroup, ToggleButton, Divider, CircularProgress,
  Paper, Chip, LinearProgress,
} from '@mui/material';
import { myConstants } from '../../shares/Constants';
import { useFetchTournamentQuery } from '../../store/tournament/TournamentApi';
import NumberSpinner from '../../shares/NumberSpinner';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import HotelIcon from '@mui/icons-material/Hotel';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';

const STEPS = [
  { label: 'Unterkunft',    icon: <HotelIcon /> },
  { label: 'Verpflegung',   icon: <RestaurantIcon /> },
  { label: 'Services',      icon: <DirectionsBusIcon /> },
  { label: 'Zusammenfassung', icon: <CheckCircleOutlineIcon /> },
];

const DEFAULTS = {
  players:                    4,
  fellows:                    0,
  vacationType:               myConstants.overnight,
  firstDay:                   myConstants.fd_saturday,
  saturdayBreakfast:          0,
  sundayBreakfast:            0,
  saturdayLunch:              0,
  barSets:                    0,
  shuttleArrivalPassengers:   0,
  shuttleDeparturePassengers: 0,
  shuttleArrivalDate:         dayjs(),
  shuttleDepartureDate:       dayjs(),
};


function StepCard({ icon, title, chips = [], children }) 
{
  return (
    <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <Box sx={{
        bgcolor: 'primary.main', color: 'white',
        px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 1,
      }}>
        {icon}
        <Typography variant="h6" fontWeight={600}>{title}</Typography>
      </Box>
      {chips.length > 0 && (
        <Box sx={{ px: 3, pt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {chips}
        </Box>
      )}
      <Box sx={{ p: 3 }}>
        {children}
      </Box>
    </Paper>
  );
}


function SummaryRow({ label, value }) {
  return (
    <Box
      sx={{
        display : "flex",
        justifyContent:"space-between",
        alignItems:"center",
      
        px: 2, py: 1.2,
        '&:not(:last-child)': {
          borderBottom: '1px solid',
          borderColor: 'divider',
        },
        '&:nth-of-type(even)': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600}>{value}</Typography>
    </Box>
  );
}


export function TeamWizard({ year, club, teamLabel, teamCounter, onSubmit, isLoading }) 
{
  const [activeStep, setActiveStep] = React.useState(0);
  const { data: tournament, isLoading: isTournamentLoading } = useFetchTournamentQuery(year);
  const [form, setForm] = React.useState(DEFAULTS);

  const set = (field) => (value) => setForm((f) => ({ ...f, [field]: value }));

  const isOvernight           = form.vacationType === myConstants.overnight;
  const isFriday              = form.firstDay === myConstants.fd_friday;
  const hasSaturdayBreakfast  = isOvernight && isFriday;
  const hasSundayBreakfast    = isOvernight;

  /*
   * Unterscheidung zwischen Hanauern und Gästen bei den 
   * Preisen für die möglichen Services
   */ 
  const feeFor = (field, fieldLocal) =>
    isTournamentLoading || !club ? '...' :
    parseFloat(club.is_local ? tournament?.[0]?.[fieldLocal] : tournament?.[0]?.[field]).toFixed(2);

  /* Anzahl der noch vorhandenen Biertisch-Garnituren */
  const barSetsAvailable = tournament?.[0]?.bar_sets_available != null
    ? tournament[0].bar_sets_available
    : '?';

  const vacationLabel = {
    [myConstants.saturday]:  'Tagesgäste (nur Samstag)',
    [myConstants.weekend]:   'Wochenendgäste (Sa + So)',
    [myConstants.overnight]: 'Übernachtungsgäste (Sa + So)',
  }[form.vacationType];


    // Chips, die ab Schritt 1 angezeigt werden (Zusammenfassung Schritt 0)
  const step0Chips = [
    <Chip key="vac" label={vacationLabel} color="primary" size="small" />,
    isOvernight && <Chip key="day" label={isFriday ? 'Anreise Freitag' : 'Anreise Samstag'} color="warning" size="small" />,
    <Chip key="pl"  label={`${form.players} SpielerInnen`} color="success" size="small" />,
    form.fellows > 0 && <Chip key="fe" label={`${form.fellows} Mitreisende`} color="success" size="small" />,
  ].filter(Boolean);

  // Chips, die ab Schritt 2 zusaetzlich angezeigt werden (Zusammenfassung Schritt 1)
  const step1Chips = [
    hasSaturdayBreakfast && form.saturdayBreakfast > 0 && <Chip key="sb" label={`Frühstück Sa: ${form.saturdayBreakfast}`} size="small" variant="outlined" />,
    hasSundayBreakfast && form.sundayBreakfast   > 0 && <Chip key="su" label={`Frühstück So: ${form.sundayBreakfast}`}  size="small" variant="outlined" />,
    form.saturdayLunch > 0 && <Chip key="lu" label={`Abendessen: ${form.saturdayLunch}`} size="small" variant="outlined" />,
  ].filter(Boolean);


  const handleSubmit = () => {
    onSubmit({
      players:               parseInt(form.players),
      fellows:               parseInt(form.fellows),
      vacationType:          form.vacationType,
      firstDay:              form.firstDay,
      saturdayBreakfast:     parseInt(form.saturdayBreakfast),
      sundayBreakfast:       parseInt(form.sundayBreakfast),
      fridayLunch:           0,
      saturdayLunch:         parseInt(form.saturdayLunch),
      barSets:               parseInt(form.barSets),
      shuttleArrivingCount:  parseInt(form.shuttleArrivalPassengers),
      shuttleArrivingDate:   form.shuttleArrivalDate.toISOString(),
      shuttleDepartingCount: parseInt(form.shuttleDeparturePassengers),
      shuttleDepartingDate:  form.shuttleDepartureDate.toISOString(),
    });
  };

  const progress = (activeStep / (STEPS.length - 1)) * 100;

  return (
    <Box sx={{ width: '100%', maxWidth: 520 }}>

      {/* Stepper */}
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2, mt:3 }}>
        {STEPS.map(({ label }) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {/* Fortschrittsbalken */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{ mb: 3, borderRadius: 1, height: 6 }}
      />
 

      {/* ── Schritt 0: Unterkunft & Personen ── */}
      {activeStep === 0 && (
        <StepCard icon={<HotelIcon />} title="Unterkunft & Personen">
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                Art des Besuchs
              </Typography>
              <ToggleButtonGroup
                value={form.vacationType} exclusive fullWidth
                onChange={(e) => set('vacationType')(e.target.value)}
              >
                <ToggleButton value={myConstants.saturday}>Tagesgäste</ToggleButton>
                <ToggleButton value={myConstants.weekend}>Wochenende</ToggleButton>
                <ToggleButton value={myConstants.overnight}>Übernachtung</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {isOvernight && (
              <Box>
                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                  Tag der Anreise
                </Typography>
                <ToggleButtonGroup
                  value={form.firstDay} exclusive fullWidth
                  onChange={(e) => set('firstDay')(e.target.value)}
                >
                  <ToggleButton value={myConstants.fd_friday}>Freitagabend</ToggleButton>
                  <ToggleButton value={myConstants.fd_saturday}>Samstagmorgen</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            <NumberSpinner
              label="Anzahl SpielerInnen" min={4} size="small"
              value={form.players} onValueChange={set('players')}
            />
            <NumberSpinner
              label="Anzahl Mitreisende" size="small"
              value={form.fellows} onValueChange={set('fellows')}
            />
          </Stack>
        </StepCard>
      )}

      {/* ── Schritt 1: Verpflegung ── */}
      {activeStep === 1 && (
        <StepCard icon={<RestaurantIcon />} title="Verpflegung" chips={step0Chips}>
          <Stack spacing={3}>
            {hasSaturdayBreakfast && (
              <Box sx={{ p: 3, '& .MuiFormControl-root': { width: '100%' } }}>
                <NumberSpinner
                  label="Frühstück Samstag" min={0} size="small"
                  value={form.saturdayBreakfast} onValueChange={set('saturdayBreakfast')}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  EUR {feeFor('fee_breakfast', 'fee_breakfast_local')} / Person
                </Typography>
              </Box>
            )}
            {hasSundayBreakfast && (
              <Box sx={{ p: 3, '& .MuiFormControl-root': { width: '100%' } }}>
                <NumberSpinner
                  label="Frühstück Sonntag" min={0} size="small"
                  value={form.sundayBreakfast} onValueChange={set('sundayBreakfast')}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  EUR {feeFor('fee_breakfast', 'fee_breakfast_local')} / Person
                </Typography>
              </Box>
            )}
            <Box sx={{ p: 3, '& .MuiFormControl-root': { width: '100%' } }}>
              <NumberSpinner
                label="Abendessen Samstag (Pasta-Buffet)" min={0} size="small"
                value={form.saturdayLunch} onValueChange={set('saturdayLunch')}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                EUR {feeFor('fee_breakfast', 'fee_breakfast_local')} / Person
              </Typography>
            </Box>
            {!hasSaturdayBreakfast && !hasSundayBreakfast && (
              <Typography variant="body2" color="text.secondary">
                Frühstück ist nur für Übernachtungsgäste buchbar.
              </Typography>
            )}
          </Stack>
        </StepCard>
      )}

      {/* ── Schritt 2: Services ── */}
      {activeStep === 2 && (
        <StepCard
          icon={<DirectionsBusIcon />}
          title="Services"
          chips={[...step0Chips, ...step1Chips]}
        >
          <Stack spacing={3}>
            <Box sx={{ p: 3, '& .MuiFormControl-root': { width: '100%' } }}>
              <NumberSpinner
                label="Biergarnituren reservieren" size="small"
                value={form.barSets} onValueChange={set('barSets')}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                EUR {feeFor('fee_bar_set', 'fee_bar_set_local')} / Garnitur · Noch verfügbar: {barSetsAvailable}
              </Typography>
            </Box>

            <Divider />

            <NumberSpinner
              label="Shuttleplätze Ankunft" size="small"
              value={form.shuttleArrivalPassengers}
              onValueChange={set('shuttleArrivalPassengers')}
            />
            <DateTimePicker
              label="Ankunft am Bahnhof"
              value={form.shuttleArrivalDate}
              onChange={set('shuttleArrivalDate')}
              disabled={form.shuttleArrivalPassengers === 0}
            />

            <NumberSpinner
              label="Shuttleplätze Abfahrt" size="small"
              value={form.shuttleDeparturePassengers}
              onValueChange={set('shuttleDeparturePassengers')}
            />
            <DateTimePicker
              label="Abfahrt am Bahnhof"
              value={form.shuttleDepartureDate}
              onChange={set('shuttleDepartureDate')}
              disabled={form.shuttleDeparturePassengers === 0}
            />
          </Stack>
        </StepCard>
      )}

      {/* ── Schritt 3: Zusammenfassung ── */}
      {activeStep === 3 && (
        <StepCard icon={<CheckCircleOutlineIcon />} title="Zusammenfassung">
          <Stack spacing={3}>

            {/* Unterkunft & Personen */}
            <Box>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                Unterkunft & Personen
              </Typography>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <SummaryRow label="Unterkunft"    value={vacationLabel} />
                {isOvernight && <SummaryRow label="Anreisetag" value={isFriday ? 'Freitagabend' : 'Samstagmorgen'} />}
                <SummaryRow label="SpielerInnen"  value={form.players} />
                <SummaryRow label="Mitreisende"   value={form.fellows} />
              </Box>
            </Box>

            {/* Verpflegung */}
            <Box>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                Verpflegung
              </Typography>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                {hasSaturdayBreakfast && <SummaryRow label="Frühstück Samstag" value={form.saturdayBreakfast} />}
                {hasSundayBreakfast && <SummaryRow label="Frühstück Sonntag" value={form.sundayBreakfast} />}
                <SummaryRow label="Abendessen Samstag" value={form.saturdayLunch || '—'} />
              </Box>
            </Box>

            {/* Services */}
            <Box>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                Services
              </Typography>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <SummaryRow label="Biergarnituren"   value={form.barSets || '—'} />
                <SummaryRow label="Shuttle Ankunft"  value={form.shuttleArrivalPassengers || '—'} />
                <SummaryRow label="Shuttle Abfahrt"  value={form.shuttleDeparturePassengers || '—'} />
              </Box>
            </Box>

          </Stack>
        </StepCard>
      )}

      {/* Navigation */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <Button
          variant="outlined"
          disabled={activeStep === 0}
          onClick={() => setActiveStep((step) => step - 1)}
          sx={{borderRadius: 20, px:3}}
          size="small"
        >
          ← Zurück
        </Button>
        {activeStep < STEPS.length - 1 ? (
          <Button 
            variant="contained" 
            onClick={() => setActiveStep((step) => step + 1)}
            size="small"
            sx={{borderRadius: 20, px:3}}
          >
            Weiter →
          </Button>
        ) : (
          <Button 
            variant="contained" 
            size="small"
            sx={{borderRadius: 20, px:3}}
            onClick={handleSubmit} 
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : 'Team anmelden'}
          </Button>
        )}
      </Box>
    </Box>
  );
}